import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory, useParams } from "react-router-dom";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import { useQuery } from "@apollo/client";
import { GET_ALL_EXAM_PAPERS } from "../../../../utils/graphql/services/ai-evaluation/queries";
import { Alert, Snackbar } from "@mui/material";
import ExamPaperTable from "./exam-paper-question-view";

type CreateExamInput = {
  title: string;
  questionPaperPublicPdfUrl: string;
  scheduledDate: string;
  totalSections: number;
  totalMarks: number;
  totalQuestions: number;
  questionTypes: string[];
  bloomsTaxonomyOne: string[];
  bloomsTaxonomyTwo: string[];
  isAutogradeEnabled: boolean;
  isAutomarksEnabled: boolean;
};

const DEFAULT_INPUT: CreateExamInput = {
  title: "",
  questionPaperPublicPdfUrl: "",
  scheduledDate: "",
  totalSections: 0,
  totalMarks: 0,
  totalQuestions: 0,
  questionTypes: [],
  bloomsTaxonomyOne: [],
  bloomsTaxonomyTwo: [],
  isAutogradeEnabled: true,
  isAutomarksEnabled: false,
};

const QUESTION_TYPE_OPTIONS = [
  "MCQ",
  "Descriptive",
  "True/False",
  "Fill in the blanks",
];

const BLOOMS_TAXONOMY1 = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
];
const BLOOMS_TAXONOMY2 = [
  "Factual",
  "Conceptual",
  "Procedural",
  "Metacognitive",
];

function ViewExamPaperPage() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<CreateExamInput>(DEFAULT_INPUT);
  const [originalData, setOriginalData] =
    useState<CreateExamInput>(DEFAULT_INPUT);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const { data, loading, error } = useQuery(GET_ALL_EXAM_PAPERS, {
    client: autoGradeClient,
    variables: { input: { id: Number(id) } },
    skip: !id,
  });

  useEffect(() => {
    if (data?.getExamPapers?.examPapers?.[0]) {
      const examPaper = data.getExamPapers.examPapers[0];

      let formattedDate = examPaper.scheduledDate;
      if (formattedDate) {
        formattedDate = new Date(formattedDate).toISOString().slice(0, 16);
      }

      const displayQuestionTypes = (examPaper.questionTypes || []).map(
        (qt: string) => QUESTION_TYPE_OPTIONS[qt] || qt
      );

      const formData = {
        title: examPaper.title || "",
        questionPaperPublicPdfUrl: examPaper.questionPaperPublicPdfUrl || "",
        scheduledDate: formattedDate || "",
        totalSections: examPaper.totalSections || 0,
        totalMarks: examPaper.totalMarks || 0,
        totalQuestions: examPaper.totalQuestions || 0,
        questionTypes: displayQuestionTypes,
        bloomsTaxonomyOne: Array.isArray(examPaper.bloomsTaxonomyOne)
          ? examPaper.bloomsTaxonomyOne
          : examPaper.bloomsTaxonomyOne
          ? [examPaper.bloomsTaxonomyOne]
          : [],
        bloomsTaxonomyTwo: Array.isArray(examPaper.bloomsTaxonomyTwo)
          ? examPaper.bloomsTaxonomyTwo
          : examPaper.bloomsTaxonomyTwo
          ? [examPaper.bloomsTaxonomyTwo]
          : [],
        isAutogradeEnabled: examPaper.isAutogradeEnabled ?? true,
        isAutomarksEnabled: examPaper.isAutomarksEnabled ?? false,
      };

      setForm(formData);
      setOriginalData(formData);

      if (examPaper.questionPaperPublicPdfUrl) {
        const urlParts = examPaper.questionPaperPublicPdfUrl.split("/");
        setUploadedFileName(urlParts[urlParts.length - 1]);
      }
    }
  }, [data]);

  const handleChange = (field: keyof CreateExamInput, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading)
    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
            >
              <CircularProgress />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );

  if (error)
    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              minHeight={400}
              justifyContent="center"
            >
              <Typography color="error" variant="h6" gutterBottom>
                Error loading exam paper
              </Typography>
              <Typography color="textSecondary">{error.message}</Typography>
              <Button
                variant="outlined"
                onClick={() => history.goBack()}
                style={{ marginTop: 16 }}
              >
                Go Back
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );

  if (!data?.getExamPapers?.examPapers?.[0])
    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              minHeight={400}
              justifyContent="center"
            >
              <Typography variant="h6" gutterBottom>
                Exam paper not found
              </Typography>
              <Button
                variant="outlined"
                onClick={() => history.goBack()}
                style={{ marginTop: 16 }}
              >
                Go Back
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Paper elevation={3} style={{ padding: 16 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">View Exam Paper #{id}</Typography>
          </Box>
          <Grid container spacing={2} component="form">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                aria-readonly
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {form.questionPaperPublicPdfUrl && (
                <TextField
                  fullWidth
                  margin="dense"
                  label="Question Paper PDF URL"
                  value={form.questionPaperPublicPdfUrl}
                  aria-readonly
                />
              )}
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Scheduled Date"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.scheduledDate}
                aria-readonly
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Sections"
                value={form.totalSections}
                inputProps={{ min: 1 }}
                aria-readonly
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Marks"
                value={form.totalMarks}
                inputProps={{ min: 1 }}
                aria-readonly
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Questions"
                value={form.totalQuestions}
                inputProps={{ min: 1 }}
                aria-readonly
              />
            </Grid>
            <Grid item xs={12} md={8} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Question Types</InputLabel>
                <Select
                  multiple
                  value={form.questionTypes}
                  input={<OutlinedInput label="Question Types" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  readOnly
                >
                  {QUESTION_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Bloom's Taxonomy One</InputLabel>
                <Select
                  multiple
                  value={form.bloomsTaxonomyOne}
                  input={<OutlinedInput label="Bloom's Taxonomy One" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  readOnly
                >
                  {BLOOMS_TAXONOMY1.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Bloom's Taxonomy Two</InputLabel>
                <Select
                  multiple
                  value={form.bloomsTaxonomyTwo}
                  input={<OutlinedInput label="Bloom's Taxonomy Two" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  readOnly
                >
                  {BLOOMS_TAXONOMY2.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={form.isAutogradeEnabled} />}
                label="Enable Auto-grade"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={form.isAutomarksEnabled} />}
                label="Enable Auto-marks"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => history.goBack()}
                  style={{ marginRight: 8 }}
                >
                  Close
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          • Exam Paper Question List
        </Typography>
        <ExamPaperTable examPaperId={Number(id)} />
      </Grid>
    </Grid>
  );
}

export default ViewExamPaperPage;
